'use client';

import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  getSeoTextCollapseLimit,
  shouldCollapseSeoText,
} from '@/features/seo-listing/utils/seo-listing';

type SeoListingTextProps = {
  text: string | null;
  variant: 'top' | 'bottom';
  eyebrow?: string;
  heading?: string;
};

export function SeoListingText({ text, variant, eyebrow, heading }: SeoListingTextProps) {
  const [isOpen, setIsOpen] = useState(false);
  const trimmed = text?.trim() ?? '';
  const canCollapse = variant === 'bottom' && shouldCollapseSeoText(trimmed);
  const preview = useMemo(() => trimmed.slice(0, getSeoTextCollapseLimit()).trim(), [trimmed]);
  if (trimmed.length === 0) return null;
  if (variant === 'top') {
    return (
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-[#6E7384] dark:text-[#B4BACD]">
        {trimmed}
      </div>
    );
  }
  const resolvedHeading = heading?.trim() || 'Описание';
  const bodyText = canCollapse && !isOpen ? `${preview}…` : trimmed;
  const toggleLabel = isOpen ? 'Свернуть' : 'Показать ещё';
  return (
    <Collapsible
      open={canCollapse ? isOpen : true}
      onOpenChange={setIsOpen}
      className="rounded-[16px] bg-white px-5 py-6 shadow-sm sm:px-8 sm:py-8"
    >
      <div className="flex flex-col gap-3">
        {eyebrow?.trim() ? (
          <p className="text-sm font-medium uppercase leading-[130%] tracking-[0.04em] text-[#6E7384]">
            {eyebrow.trim()}
          </p>
        ) : null}
        <h2 className="text-[28px] font-black leading-[1.1] tracking-[-0.02em] text-[#1B1D22] dark:text-white sm:text-[32px]">
          {resolvedHeading}
        </h2>
        <div className="whitespace-pre-wrap text-sm font-medium leading-[150%] tracking-[-0.01em] text-[#575E74] dark:text-[#B4BACD] sm:text-base">
          {bodyText}
        </div>
        {canCollapse ? (
          <CollapsibleTrigger className="inline-flex w-fit items-center gap-1 text-sm font-medium text-[#505CFF] hover:text-[#505CFF]/80">
            {toggleLabel}
            {isOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          </CollapsibleTrigger>
        ) : null}
      </div>
      <CollapsibleContent />
    </Collapsible>
  );
}
